/**
 * ============================================================================
 * usePromotions - PROMOTIONS DATA FETCHING HOOK WITH CRUD MUTATIONS
 * ============================================================================
 *
 * Production-ready TanStack Query hook for promotions with full CRUD support
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type {
  Promotion,
  PromotionStats,
  PromotionFilters,
  CreatePromotionData,
  UpdatePromotionData,
} from '../types/promotions';

interface PromotionsDataResponse {
  promotions: Promotion[];
  stats: PromotionStats;
  totalCount: number;
}

/**
 * Fetches promotions with stats and filtering
 */
export function usePromotions(venueId: number | undefined, filters: PromotionFilters) {
  return useQuery<PromotionsDataResponse, Error>({
    queryKey: ['promotions', venueId, filters.status, filters.sport, filters.search],

    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      // Fetch promotions from API
      const promotions: Promotion[] = await vendorApi.getPromotions(venueId);

      // Client-side filtering
      let filteredPromotions = promotions;

      // Status filter (active, expired, scheduled, inactive)
      if (filters.status !== 'all') {
        filteredPromotions = filteredPromotions.filter((promo: Promotion) => {
          const now = new Date();
          const validFrom = new Date(promo.valid_from);
          const validUntil = new Date(promo.valid_until);

          switch (filters.status) {
            case 'active':
              return promo.is_active && now >= validFrom && now <= validUntil;
            case 'inactive':
              return !promo.is_active;
            case 'expired':
              return now > validUntil;
            case 'scheduled':
              return now < validFrom;
            default:
              return true;
          }
        });
      }

      // Sport filter
      if (filters.sport !== 'all') {
        filteredPromotions = filteredPromotions.filter(
          (promo: Promotion) => promo.sport_id === filters.sport
        );
      }

      // Search filter
      if (filters.search.trim()) {
        const query = filters.search.toLowerCase();
        filteredPromotions = filteredPromotions.filter(
          (promo: Promotion) =>
            promo.title.toLowerCase().includes(query) ||
            promo.description?.toLowerCase().includes(query)
        );
      }

      // Calculate stats
      const stats = calculateStats(promotions);

      return {
        promotions: filteredPromotions,
        stats,
        totalCount: filteredPromotions.length,
      };
    },

    enabled: !!venueId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes in cache
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Calculate stats from promotions list
 */
function calculateStats(promotions: Promotion[]): PromotionStats {
  const now = new Date();

  return {
    totalPromotions: promotions.length,
    activePromotions: promotions.filter((p) => {
      const validFrom = new Date(p.valid_from);
      const validUntil = new Date(p.valid_until);
      return p.is_active && now >= validFrom && now <= validUntil;
    }).length,
    scheduledPromotions: promotions.filter((p) => now < new Date(p.valid_from)).length,
    expiredPromotions: promotions.filter((p) => now > new Date(p.valid_until)).length,
  };
}

/**
 * Create a new promotion
 */
export function useCreatePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      data,
    }: {
      venueId: number;
      data: CreatePromotionData;
    }) => {
      return vendorApi.createPromotion(venueId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/**
 * Update an existing promotion
 */
export function useUpdatePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      promotionId,
      data,
    }: {
      venueId: number;
      promotionId: number;
      data: UpdatePromotionData;
    }) => {
      return vendorApi.updatePromotion(venueId, promotionId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/**
 * Delete a promotion
 */
export function useDeletePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      promotionId,
    }: {
      venueId: number;
      promotionId: number;
    }) => {
      return vendorApi.deletePromotion(venueId, promotionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/**
 * Toggle promotion active status
 */
export function useTogglePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      promotionId,
    }: {
      venueId: number;
      promotionId: number;
    }) => {
      return vendorApi.togglePromotion(venueId, promotionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}
